import _ from "lodash"
import * as wjCore from "@grapecity/wijmo"
import {
  TZyomuinHibetuKintai,
  TZyomuinSyukkinboYukyuByMonthGetRes,
} from "../../../service/attendance/syukkinbo_v2/TZyomuinSyukkinbo"
import dateUtil from "../../../ts/util/dateUtil"
import { e乗務員所定労働時間区分, e勤怠区分 } from "../../../constant/enum/unko"
import dayjs from "dayjs"
import { T勤怠日数集計 } from "./SummaryWorkingDayGrid"
import { T勤怠集計 } from "./SummaryTimeGrid"
import { T勤怠リスト } from "./ZyomuinSyukkinbo"
import { T区分マスタ } from "../../../components/ComboBox/KubunComboBox"
import { 区分分類コード } from "../../../constant/enum/codes"
import {
  abstractExcelFormat,
  abstractPdfFormat,
  TExportColumns,
} from "../../../ts/util/exportUtil"

export const e労務警告区分 = {
  出勤打刻未存在かつデジタコ出庫存在: "10",
  出勤打刻存在かつ退勤打刻未存在: "11",
  電話点呼時中間点呼未存在: "20",
  休息3時間未満: "21",
  拘束時間13時間超過: "30",
  拘束時間14時間超過: "31",
  連続走行時間4時間以上: "40",
} as const

export const create勤怠時間総計 = (
  勤怠リスト: TZyomuinHibetuKintai[]
): T勤怠集計[] => {
  return [
    {
      勤務時間: dateUtil.toTimeSpanString(
        _.sumBy(勤怠リスト, (i) => parseInt(i.勤務時間 ?? "0") ?? 0)
      ),
      拘束時間: dateUtil.toTimeSpanString(
        _.sumBy(勤怠リスト, (i) => parseInt(i.拘束時間 ?? "0") ?? 0)
      ),
      残業時間: dateUtil.toTimeSpanString(
        _.sumBy(勤怠リスト, (i) => parseInt(i.残業時間 ?? "0") ?? 0)
      ),
      休出時間: dateUtil.toTimeSpanString(
        _.sumBy(勤怠リスト, (i) => parseInt(i.休出時間 ?? "0") ?? 0)
      ),
      深夜時間: dateUtil.toTimeSpanString(
        _.sumBy(勤怠リスト, (i) => parseInt(i.深夜時間 ?? "0") ?? 0)
      ),
    },
  ]
}

/**
 * 勤務区分ドロップダウンの表示順
 * 区分マスタにソート番号が設定されておらず、取得SQL（VKubunDao.SelectBy）の
 * ORDER BY が全件同値になるため、並び順がDBの物理並び順に依存し環境ごとに変わる。
 * （本番と検証で「特別休暇」「所定出勤」の前後が入れ替わっていた: KAIROS-1125）
 * そのため画面側で表示順を固定する。育介休暇・育介半休は欠勤の下（最下部）に配置する。
 * 区分マスタのソート番号を設定すると事務員出勤簿など他画面にも波及するため、
 * マスタではなく乗務員出勤簿の画面側でのみ並び替える方式とした。
 */
const 勤務区分表示順List: string[] = [
  e勤怠区分.出勤,
  e勤怠区分.有給休暇,
  e勤怠区分.特別休暇,
  e勤怠区分.所定出勤,
  e勤怠区分.所定休日,
  e勤怠区分.休日,
  e勤怠区分.振替休日,
  e勤怠区分.代休,
  e勤怠区分.欠勤,
  e勤怠区分.育介休暇,
  e勤怠区分.育介半休,
]

/**
 * 勤務区分ドロップダウンの表示順を取得する
 * 表示順Listに無い区分（今後マスタに追加された区分）は末尾へ寄せる。
 * 該当分は比較結果が一律0となり、安定ソートによりマスタの並び順がそのまま保たれる。
 * @param 区分コード 勤怠区分コード
 */
const get勤務区分表示順 = (区分コード: string): number => {
  const index = 勤務区分表示順List.indexOf(区分コード)
  return index < 0 ? 勤務区分表示順List.length : index
}

/**
 * 勤務区分ドロップダウン用の区分リストを生成する
 * @param 区分ItemSource 区分マスタ
 */
export const create勤務区分ItemSource = (
  区分ItemSource?: T区分マスタ[]
): T区分マスタ[] =>
  (区分ItemSource ?? [])
    .filter(
      (d) =>
        d.分類コード === 区分分類コード.運行管理_勤怠.勤怠区分 &&
        // 半日区分は現状の給与計算方法では使用できないため非表示
        d.区分コード !== e勤怠区分.午前半休 &&
        d.区分コード !== e勤怠区分.午後半休 &&
        d.区分コード !== e勤怠区分.半欠勤
    )
    // 表示順は区分マスタに依存せず画面側で固定する（勤務区分表示順List のコメント参照）
    .sort(
      (a, b) =>
        get勤務区分表示順(a.区分コード) - get勤務区分表示順(b.区分コード)
    )

export const create勤務日数総計 = (
  勤怠リスト: TZyomuinHibetuKintai[],
  有給残数: TZyomuinSyukkinboYukyuByMonthGetRes
): T勤怠日数集計[] => {
  const 出勤日List: dayjs.Dayjs[] = []
  let 休出List: dayjs.Dayjs[] = []
  let 特休List: dayjs.Dayjs[] = []
  let 有休List: dayjs.Dayjs[] = []
  let 半休List: dayjs.Dayjs[] = []
  let 振替List: dayjs.Dayjs[] = []
  let 代休List: dayjs.Dayjs[] = []
  let 欠勤List: dayjs.Dayjs[] = []
  let 育介List: dayjs.Dayjs[] = []
  let 育介半休List: dayjs.Dayjs[] = []
  let 有休残日数 = 有給残数.年度開始有休日数 - 有給残数.前月有休取得数

  勤怠リスト.forEach((勤怠) => {
    if (勤怠.勤務日 == null) return

    switch (勤怠.勤怠区分コード) {
      case e勤怠区分.出勤:
        出勤日List.push(dayjs(勤怠.勤務日))
        //対象の日が日曜の場合に休日出勤にもカウント
        if (dayjs(勤怠.勤務日).day() === 0) {
          休出List.push(dayjs(勤怠.勤務日))
        }
        break
      case e勤怠区分.所定出勤:
        出勤日List.push(dayjs(勤怠.勤務日))
        break
      case e勤怠区分.特別休暇:
        特休List.push(dayjs(勤怠.勤務日))
        break
      case e勤怠区分.有給休暇:
        有休List.push(dayjs(勤怠.勤務日))
        break
      case e勤怠区分.午前半休:
      case e勤怠区分.午後半休:
        半休List.push(dayjs(勤怠.勤務日))
        break
      case e勤怠区分.振替休日:
        振替List.push(dayjs(勤怠.勤務日))
        break
      case e勤怠区分.代休:
        代休List.push(dayjs(勤怠.勤務日))
        break
      case e勤怠区分.欠勤:
        欠勤List.push(dayjs(勤怠.勤務日))
        break
      // 育介は有休を消化しないため、有休とは別に集計する
      case e勤怠区分.育介休暇:
        育介List.push(dayjs(勤怠.勤務日))
        break
      case e勤怠区分.育介半休:
        育介半休List.push(dayjs(勤怠.勤務日))
        break
      default:
        break
    }
  })

  const 出勤日数 = _.uniqBy(出勤日List, (item) =>
    item.format("YYYY-MM-DD")
  ).length
  const 休出日数 = _.uniqBy(休出List, (item) =>
    item.format("YYYY-MM-DD")
  ).length
  const 特休日数 = _.uniqBy(特休List, (item) =>
    item.format("YYYY-MM-DD")
  ).length
  const 有休日数 =
    _.uniqBy(有休List, (item) => item.format("YYYY-MM-DD")).length +
    _.uniqBy(半休List, (item) => item.format("YYYY-MM-DD")).length * 0.5
  const 振替日数 = _.uniqBy(振替List, (item) =>
    item.format("YYYY-MM-DD")
  ).length
  const 代休日数 = _.uniqBy(代休List, (item) =>
    item.format("YYYY-MM-DD")
  ).length
  const 欠勤日数 = _.uniqBy(欠勤List, (item) =>
    item.format("YYYY-MM-DD")
  ).length
  const 育介日数 =
    _.uniqBy(育介List, (item) => item.format("YYYY-MM-DD")).length +
    _.uniqBy(育介半休List, (item) => item.format("YYYY-MM-DD")).length * 0.5

  return [
    {
      出勤日数,
      休出日数,
      特休日数,
      有休日数,
      振替日数,
      代休日数,
      欠勤日数,
      育介日数,
      有休残日数: 有休残日数 - 有休日数,
    },
  ]
}

/**
 * 勤怠区分を確認し、勤務時間、休出時間、残業時間のみ再計算する
 * @param data
 */
export const calc勤怠リスト = (
  data: T勤怠リスト,
  区分リスト: T区分マスタ[]
) => {
  const 所定労働時間区分_基準労働時間 = 区分リスト.find(
    (e) =>
      e.分類コード === 区分分類コード.運行管理_勤怠.乗務員所定労働時間区分 &&
      e.区分コード === e乗務員所定労働時間区分.基準労働時間
  )

  const 所定労働時間Milliseconds =
    所定労働時間区分_基準労働時間?.数値1 == null
      ? 0
      : 所定労働時間区分_基準労働時間?.数値1 * 1000 * 60 * 60

  if (
    wjCore.isNullOrWhiteSpace(data.タイムカード出勤時刻 ?? "") ||
    wjCore.isNullOrWhiteSpace(data.タイムカード退勤時刻 ?? "")
  ) {
    data.拘束時間 = ""
    data.勤務時間 = ""
    data.残業時間 = ""
    data.休出時間 = ""
    return
  }

  const str出勤時刻 = wjCore.isNullOrWhiteSpace(data.タイムカード出勤時刻 ?? "")
    ? data.始業時刻!
    : data.タイムカード出勤時刻!
  const str終業時刻 = wjCore.isNullOrWhiteSpace(data.タイムカード退勤時刻 ?? "")
    ? data.終業時刻!
    : data.タイムカード退勤時刻!

  const 退勤日時 = dayjs(data?.勤務日)
    .startOf("day")
    .add(dateUtil.toMilliseconds(str終業時刻), "milliseconds")
  const 出勤日時 = dayjs(data?.勤務日)
    .startOf("day")
    .add(dateUtil.toMilliseconds(str出勤時刻), "milliseconds")

  const 拘束時間 = 退勤日時.diff(出勤日時)
  const 休憩時間 =
    Number.isNaN(data.休憩時間) || data.休憩時間 == null
      ? 0
      : parseInt(data.休憩時間)
  const 勤務時間 = 拘束時間 - 休憩時間

  let 残業時間 = 0
  if (出勤日時.day() !== 0) {
    //TODO: 所定出勤作成後に区分を変更
    if (data.勤怠区分コード === e勤怠区分.所定出勤) {
      残業時間 = 勤務時間
    } else if (所定労働時間Milliseconds < 勤務時間) {
      残業時間 = 勤務時間 - 所定労働時間Milliseconds
    }
  } else {
    data.休出時間 = 勤務時間.toString()
  }

  data.拘束時間 = 拘束時間.toString()
  data.勤務時間 = 勤務時間.toString()
  data.残業時間 = 残業時間.toString()
}

export const 出勤無し勤怠区分List: string[] = [
  e勤怠区分.有給休暇,
  e勤怠区分.特別休暇,
  e勤怠区分.所定休日,
  e勤怠区分.育介休暇,
  e勤怠区分.休日,
  e勤怠区分.慶忌休暇,
  e勤怠区分.振替休日,
  e勤怠区分.欠勤,
]

/**
 * 日跨ぎしたレコードかどうかをチェックする
 * @param data T勤怠リスト
 */
export const is日跨ぎレコード = (data: T勤怠リスト) => {
  return (
    wjCore.isNullOrWhiteSpace(data.タイムカード出勤時刻 ?? "") &&
    !wjCore.isNullOrWhiteSpace(data.タイムカード退勤時刻 ?? "") &&
    !wjCore.isNullOrWhiteSpace(data.始業時刻 ?? "") &&
    data.デジタコ勤怠ID == null
  )
}

// お知らせ
const 勤怠リストcolumns: TExportColumns<keyof T勤怠リスト> = {
  DateColumns: ["勤務日"],
}
export const 乗務員出勤簿ExcelFormat = abstractExcelFormat(勤怠リストcolumns)
export const 乗務員出勤簿PdfFormat = abstractPdfFormat(勤怠リストcolumns)
